import { translateKey, type Translate } from "../i18n/translate";
import { SOURCE_LANGUAGE } from "../i18n/languages";

interface ThrownApiError {
  code?: unknown;
  body?: { message?: unknown; params?: unknown };
}

/** The structured error code attached by the http layer, if any. */
export function errorCode(e: unknown): string | undefined {
  if (typeof e !== "object" || e === null) return undefined;
  const code = (e as ThrownApiError).code;
  return typeof code === "string" ? code : undefined;
}

function serverMessage(e: unknown): string | undefined {
  if (typeof e !== "object" || e === null) return undefined;
  const message = (e as ThrownApiError).body?.message;
  return typeof message === "string" && message.trim() !== ""
    ? message
    : undefined;
}

function serverParams(e: unknown): Record<string, unknown> {
  if (typeof e !== "object" || e === null) return {};
  const params = (e as ThrownApiError).body?.params;
  return typeof params === "object" && params !== null
    ? (params as Record<string, unknown>)
    : {};
}

/**
 * In English the server's own sentence wins: it is usually more specific
 * ("Title not found.") than `en/errors.json`, which covers replies without one.
 */
export function knownErrorMessage(
  t: Translate,
  e: unknown
): string | undefined {
  const code = errorCode(e);
  if (!code) return undefined;
  const key = `errors:${code}`;
  const params = serverParams(e);
  const message = serverMessage(e);
  const found = (text: string) => text !== "" && text !== key && text !== code;
  const translated = translateKey(t, key, { ...params, defaultValue: "" });
  const english = translateKey(t, key, {
    ...params,
    lng: SOURCE_LANGUAGE,
    defaultValue: "",
  });
  if (message && (!found(translated) || translated === english)) {
    return message;
  }
  return found(translated) ? translated : undefined;
}

/** What to show for any thrown error: the known sentence, else its message. */
export function errorText(e: unknown, t: Translate): string {
  return (
    knownErrorMessage(t, e) ??
    (e instanceof Error ? e.message : typeof e === "string" ? e : String(e))
  );
}

/** The known sentence for an API error, else `fallback`, else its message. */
export function friendlyError(
  t: Translate,
  e: unknown,
  fallback?: string
): string {
  return knownErrorMessage(t, e) ?? fallback ?? errorText(e, t);
}
